import { useNavigate } from 'react-router';
import FileSaver from 'file-saver';
import {
    Box,
    Flex,
    Heading,
    Text,
    Button,
    Icon,
    useToast,
    Select,
    Modal,
    ModalContent,
    ModalOverlay,
    ModalHeader,
    ModalFooter,
    ModalCloseButton,
    ModalBody,
    useDisclosure,
    chakra,
} from '@chakra-ui/react';
import PageHeader from 'layout/pageHeader';
import MDropZone from 'components/molecules/MDropZone';
import { ChangeEvent, useRef, useState } from 'react';
import { EXCEL_FILE_TYPE } from 'shared/const';
import icons from 'shared/icons';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { linkStyle } from 'shared/styles';
import { isFetchBaseQueryError } from 'utils/erros';
import { useTranslation } from 'helpers/languages/translations';
import { IProductKeywordsExcelData } from 'types';
import {
    useUploadProductKeywordsMutation,
    useLazyExportProductKeywordsQuery,
    useExportProductKeywordsErrorsMutation,
} from 'core/API/productKeywordsApi';
import { IProductKeywordUploadError } from '../types';

const ProductKeywordsUpload = () => {
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadProductKeywords, { isLoading }] = useUploadProductKeywordsMutation({
        fixedCacheKey: 'product-keywords-upload-key',
    });
    const [faultyRowCount, setFaultyRowCount] = useState<number>(0);
    const [modalType, setModalType] = useState<'upload' | 'error'>('upload');
    const [errorList, setErrorList] = useState<IProductKeywordsExcelData[]>([]);
    const [exportProductKeywords] = useLazyExportProductKeywordsQuery();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const navigate = useNavigate();
    const translate = useTranslation();
    const goBack = () => {
        navigate(-1);
    };
    const [exportProductKeywordsErrors] = useExportProductKeywordsErrorsMutation();
    const toast = useToast();
    const overwriteInitialRef = useRef(null);
    const [uploadMethod, setUploadMethod] = useState<'keep' | 'overwrite'>('keep');
    const handleChangeFile = (file: File | null) => {
        if (file?.type === EXCEL_FILE_TYPE) {
            setUploadedFile(file);
        } else {
            if (file !== null)
                toast({
                    title: translate('error.upload.file-invalid-type.xlsx'),
                    status: 'error',
                    isClosable: true,
                });
            setUploadedFile(null);
        }
    };

    const handleChangeUploadMethod = (e: ChangeEvent<HTMLSelectElement>) => {
        setUploadMethod(e.target.value as 'keep' | 'overwrite');
    };

    const handleDownloadErrorFile = async () => {
        try {
            const res = await exportProductKeywordsErrors(errorList);
            if ('error' in res) throw new Error(translate('error.export') as string);
            const blob = new Blob([res.data], {
                type: EXCEL_FILE_TYPE,
            });
            FileSaver.saveAs(blob, translate('product_keywords.export.error_file') as string);
        } catch (error) {
            toast({
                title: (error as Error).message || translate('error.generic'),
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderDescriptionSection = () => {
        const UploadIcon = icons.upload;

        const handleUploadKeywords = async () => {
            try {
                const bodyFormData = new FormData();
                bodyFormData.append('excelFile', uploadedFile as File);
                bodyFormData.append('uploadMethod', uploadMethod);
                const res = (await uploadProductKeywords(bodyFormData)) as IProductKeywordUploadError;
                if (res.error) {
                    const errorType = res.error?.data?.type;
                    if (errorType === 'missing_headers') {
                        const missingHeaders = res.error?.data?.missing_headers;

                        if (missingHeaders?.length)
                            throw new Error(
                                translate('keywords.error.missing_specific_headers', {
                                    headers: missingHeaders.join(', '),
                                }) as string,
                            );
                        else throw new Error(translate('upload.error.missing_headers') as string);
                    }

                    if (errorType === 'invalid_data') {
                        const data = res.error?.data?.data;
                        if (data) setErrorList(data);
                        setFaultyRowCount(res.error?.data?.faulty_row_count || 0);
                        setModalType('error');
                        onOpen();
                        return;
                    }
                }
                goBack();
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const handleDownloadTemplate = async () => {
            try {
                const { data: res, isError: isExportError, error } = await exportProductKeywords({ isTemplate: true });

                if (isExportError || !res) {
                    if (isFetchBaseQueryError(error)) {
                        const errorMsg = error.data as string;
                        throw new Error(errorMsg);
                    }

                    throw new Error(translate('product_keyword.upload.error.downloadTemplate') as string);
                }

                const blob = new Blob([res], {
                    type: EXCEL_FILE_TYPE,
                });

                FileSaver.saveAs(blob, translate('product_keyword.upload.templateFileName') as string);
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const renderOverwriteModal = () => {
            const uploadTitle =
                uploadMethod === 'overwrite'
                    ? translate('keyword.upload.modal.overwriteHeader')
                    : translate('product_keyword.upload.modal.disableHeader');
            const title = modalType === 'upload' ? uploadTitle : translate('upload.modal.error.title');

            const uploadBodyContent =
                uploadMethod === 'overwrite' ? (
                    <>
                        <Text>{translate('product_keyword.upload.modal.overwriteDesc1')}</Text>
                        <Text>{translate('keyword.upload.modal.overwriteDesc2')}</Text>
                    </>
                ) : (
                    <Text>{translate('product_keyword.upload.modal.disableDesc')}</Text>
                );
            const errorBodyContent = (
                <Text>
                    {translate('page.keyword.errorsUploadedDescription', {
                        nb: faultyRowCount,
                        downloadComp: (
                            <chakra.span
                                onClick={intentionalyFloatingPromiseReturn(handleDownloadErrorFile)}
                                {...linkStyle}
                            >
                                {translate('page.keyword.errorDownload')}
                            </chakra.span>
                        ),
                    })}
                </Text>
            );
            const bodyContent = modalType === 'upload' ? uploadBodyContent : errorBodyContent;
            return (
                <Modal isCentered initialFocusRef={overwriteInitialRef} isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader tabIndex={-1} ref={overwriteInitialRef}>
                            <Text as="h3" color={modalType === 'error' ? 'red.600' : 'gray.900'}>
                                {title}
                            </Text>
                        </ModalHeader>
                        <ModalCloseButton mt="2" isDisabled={isLoading} />
                        <ModalBody fontSize="sm">{bodyContent}</ModalBody>

                        <ModalFooter>
                            <Button isDisabled={isLoading} variant="outline" size="sm" mr={3} onClick={onClose}>
                                {translate('keyword.upload.modal.cancel')}
                            </Button>
                            {uploadMethod === 'overwrite' && (
                                <Button
                                    onClick={intentionalyFloatingPromiseReturn(handleUploadKeywords)}
                                    isLoading={isLoading}
                                    colorScheme="red"
                                    size="sm"
                                >
                                    {translate('keyword.upload.modal.overwrite')}
                                </Button>
                            )}
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            );
        };

        const handleUploadChecker = async () => {
            if (uploadMethod === 'keep') {
                await handleUploadKeywords();
            } else {
                onOpen();
            }
        };

        return (
            <Flex w="40%" justifyContent="flex-start" direction="column" textAlign="start">
                <Text fontSize="md" mb="18px">
                    {translate('product_keyword.upload.recommendation', {
                        template: (
                            <Text
                                onClick={intentionalyFloatingPromiseReturn(handleDownloadTemplate)}
                                as="span"
                                {...linkStyle}
                            >
                                {translate('product_keyword.upload.downloadTemplate')}
                            </Text>
                        ),
                    })}
                </Text>

                <Text fontSize="md">{translate('keyword.upload.method')}</Text>
                <Select mb={8} mt={2} bg="white" value={uploadMethod} onChange={handleChangeUploadMethod}>
                    <option value="keep">{translate('product_keyword.upload.method.keep')}</option>
                    <option value="overwrite">{translate('product_keyword.upload.method.overwrite')}</option>
                </Select>
                <Text fontSize="md" pt={4} mb={2}>
                    {translate('keyword.upload.file')}
                </Text>
                <Box py={4}>
                    <MDropZone chosenFile={uploadedFile} handleChangeFile={handleChangeFile} />
                </Box>
                <Flex mt="24px" justifyContent="space-between">
                    <Button
                        isDisabled={!uploadedFile}
                        rightIcon={<Icon as={UploadIcon} />}
                        isLoading={isLoading}
                        onClick={intentionalyFloatingPromiseReturn(handleUploadChecker)}
                        colorScheme="blue"
                        bg="blueMain"
                    >
                        {translate('keyword.upload.apply')}
                    </Button>
                </Flex>
                {renderOverwriteModal()}
            </Flex>
        );
    };

    return (
        <Box>
            <PageHeader
                pageTitle={translate('product_keyword.upload.title') as string}
                icon={{ name: 'arrowBack', props: { onClick: goBack } }}
            />
            <Box px="32px">
                <Flex justifyContent="start">
                    <Heading as="h2" fontWeight="semibold" fontSize="18px" mb="18px">
                        {translate('keyword.upload.header')}
                    </Heading>
                </Flex>
                <Flex fontSize="sm" direction="row">
                    {renderDescriptionSection()}
                </Flex>
            </Box>
        </Box>
    );
};

export default ProductKeywordsUpload;
